/**
 * SettingsItem Component
 *
 * Reusable settings row with label, optional description, and action.
 * Supports different interaction types (navigation, action, toggle).
 */
import { View, Text, Pressable } from "react-native";

interface SettingsItemProps {
  label: string;
  description?: string;
  icon?: string;
  value?: string;
  badge?: string; // Optional badge for highlighting (e.g., "Family Plan", "Premium")
  onPress?: () => void;
  destructive?: boolean;
  selected?: boolean; // Visual indicator for selected state (e.g., language options)
  testID?: string;
}

export function SettingsItem({
  label,
  description,
  icon,
  value,
  badge,
  onPress,
  destructive = false,
  selected = false,
  testID,
}: SettingsItemProps) {
  const content = (
    <View className="flex-row items-center py-3">
      {icon && (
        <Text className="text-xl mr-3">{icon}</Text>
      )}
      <View className="flex-1">
        <View className="flex-row items-center gap-2">
          <Text
            className={`text-base font-medium ${
              destructive ? "text-red-600" : "text-gray-900"
            }`}
          >
            {label}
          </Text>
          {badge && (
            <View className="bg-purple-100 px-2 py-0.5 rounded-full">
              <Text className="text-purple-700 text-xs font-semibold">
                {badge}
              </Text>
            </View>
          )}
        </View>
        {description && (
          <Text className="text-sm text-gray-500 mt-0.5">
            {description}
          </Text>
        )}
      </View>
      {value && (
        <Text className="text-sm text-gray-500 ml-2">{value}</Text>
      )}
      {selected && (
        <Text className="text-green-600 ml-2">✓</Text>
      )}
      {onPress && !selected && (
        <Text className="text-gray-400 ml-2">›</Text>
      )}
    </View>
  );

  if (onPress) {
    return (
      <Pressable
        onPress={onPress}
        className="px-4 bg-white active:bg-gray-50"
        style={{ cursor: 'pointer' }}
        accessibilityRole="button"
        accessibilityLabel={label}
        testID={testID}
      >
        {content}
      </Pressable>
    );
  }

  return <View className="px-4 bg-white">{content}</View>;
}
